import { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  db 
} from '../config/firebase';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, userData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email,
        ...userData,
        createdAt: new Date().toISOString(),
        role: 'user'
      });
      
      return userCredential;
    } catch (error) {
      console.error('Signup error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('Email is already registered. Please login instead.');
        case 'auth/invalid-email':
          throw new Error('Invalid email address.');
        case 'auth/operation-not-allowed':
          throw new Error('Email/password accounts are not enabled. Please contact support.');
        case 'auth/weak-password':
          throw new Error('Password is too weak. Please use a stronger password.');
        default:
          throw new Error('Failed to create account. Please try again.');
      }
    }
  }

  async function loginWithEmail(email, password) {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/invalid-email':
          throw new Error('Invalid email address.');
        case 'auth/user-disabled':
          throw new Error('This account has been disabled.');
        case 'auth/user-not-found':
          throw new Error('No account found with this email.');
        case 'auth/wrong-password':
          throw new Error('Incorrect password.');
        default:
          throw new Error('Failed to login. Please try again.');
      }
    }
  }

  async function loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Check if user document exists, if not create it
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          name: result.user.displayName,
          photoURL: result.user.photoURL,
          createdAt: new Date().toISOString(),
          role: 'user'
        });
      }
      
      return result;
    } catch (error) {
      console.error('Google login error:', error);
      throw new Error('Failed to login with Google. Please try again.');
    }
  }

  async function loginWithFacebook() {
    try {
      const provider = new FacebookAuthProvider();
      // Add scopes for better user data
      provider.addScope('email');
      provider.addScope('public_profile');
      
      // Set custom parameters to handle domain issues
      provider.setCustomParameters({
        display: 'popup',
        auth_type: 'rerequest'
      });
      
      const result = await signInWithPopup(auth, provider);
      
      // Check if user document exists, if not create it
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', result.user.uid), {
          email: result.user.email,
          name: result.user.displayName,
          photoURL: result.user.photoURL,
          createdAt: new Date().toISOString(),
          role: 'user'
        });
      }
      
      return result;
    } catch (error) {
      console.error('Facebook login error:', error);
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          throw new Error('Login cancelled. Please try again.');
        case 'auth/cancelled-popup-request':
          throw new Error('Login cancelled. Please try again.');
        case 'auth/popup-blocked':
          throw new Error('Popup was blocked by your browser. Please allow popups and try again.');
        case 'auth/account-exists-with-different-credential':
          throw new Error('An account already exists with this email. Please login with your original method.');
        case 'auth/unauthorized-domain':
          throw new Error('This domain is not authorized for Facebook login. Please add kitauto.ma to your Facebook app settings.');
        case 'auth/invalid-credential-or-provider-id':
          throw new Error('Facebook login configuration error. Please check your Facebook app settings.');
        default:
          if (error.message.includes('domain')) {
            throw new Error('Domain configuration error. Please check your Facebook app settings.');
          }
          throw new Error('Failed to login with Facebook. Please try again.');
      }
    }
  }

  async function logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Failed to logout. Please try again.');
    }
  }

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Reset password error:', error);
      throw new Error('Failed to reset password');
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
    } catch (error) {
      console.error('Change password error:', error);
      throw new Error('Failed to change password');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          setCurrentUser({ ...user, ...userDoc.data() });
        } catch (error) {
          console.error('Error fetching user data:', error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    loginWithEmail,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    resetPassword,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 